import styles from './map-component.module.css';

import { REACT_APP_GOOGLE_API_KEY, ROOT_URL } from '../../state/configuration';
import { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { hideSpinner, showSpinner } from '../../state/slices/spinnerSlice';
import {
  APIProvider,
  Map,
  useMap,
  MapControl,
  ControlPosition,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import {
  GoAButton,
  GoAButtonGroup,
  GoAIcon,
  GoAInputText,
  GoAModal,
  GoASpacer,
} from '@abgov/react-components';

import {
  updateCollision,
  checkLocationStatus,
} from '../../state/slices/collisionSlice';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { useIsDesktop } from '../../utils/isDesktop';
import _, { set } from 'lodash';
import { Collision } from '../../models/collision';
import useCsrService from '../../services/csr-api-redux';
import { NotificationType } from '../notifications/notification-component';
import { useNavigate } from 'react-router-dom';

interface MapComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const MapComponent = ({
  navigationConfiguration,
}: MapComponentProps) => {
  const apiKey = REACT_APP_GOOGLE_API_KEY;
  const csrService = useCsrService();
  const dispatch = useAppDispatch();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const stateCollision = useAppSelector((state: RootState) => state.collision);

  const [nextPage, setNextPage] = useState('');
  const [locationString, setLocationString] = useState('');
  const [latLng, setLatLng] = useState<
    { lat: number; lng: number } | undefined
  >(undefined);
  const [modalType, setModalType] = useState<'confirm' | 'invalid'>('confirm');
  const [showModal, setShowModal] = useState(false);
  const [zoom, setZoom] = useState(
    stateCollision.collision.location?.zoom ?? 15,
  );
  const [enableSave, setEnableSave] = useState(true);

  const notificationMessage = 'Unable to check location status.';
  async function getLocationActiveStatus(
    lat: number,
    lng: number,
  ): Promise<boolean | undefined> {
    dispatch(removeAllValidationErrorNotifications());

    try {
      const result = await dispatch(
        checkLocationStatus({ csrService, lat, lng }),
      ).unwrap();
      return result.isActive;
    } catch (error: any) {
      console.error('Error checking location status:', error);
      dispatch(
        pushNotification({
          body: notificationMessage,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      return undefined;
    }
  }

  const handleConfirmLocation = async () => {
    if (latLng) {
      const active = await getLocationActiveStatus(latLng.lat, latLng.lng);
      if (active) {
        setModalType('confirm');
      } else {
        setModalType('invalid');
      }
      setShowModal(true);
    }
  };

  useEffect(() => {
    dispatch(showSpinner({ label: "We're looking up your location" }));

    if (
      stateCollision.collision.location &&
      stateCollision.collision.location.latitude &&
      stateCollision.collision.location.longitude
    ) {
      setLatLng({
        lat: stateCollision.collision.location.latitude,
        lng: stateCollision.collision.location.longitude,
      });
      dispatch(hideSpinner());
    } else if (
      stateCollision.collision.location &&
      stateCollision.collision.location.useLocationServices
    ) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (position) {
            setLatLng({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
            dispatch(hideSpinner());
          }
        },
        (error) => {
          setLatLng(DEFAULT_LOCATION);
          dispatch(hideSpinner());
        },
      );
    } else if (
      !stateCollision.collision.location ||
      !stateCollision.collision.location.useLocationServices
    ) {
      setLatLng(DEFAULT_LOCATION);
      dispatch(hideSpinner());
    }
  }, []);

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  const save = () => {
    if (latLng && latLng.lat && latLng.lng) {
      let updatedCollision: Collision = _.cloneDeep(stateCollision.collision);

      updatedCollision.location = {
        ...stateCollision.collision.location!,
        locationString: locationString,
        latitude: latLng!.lat,
        longitude: latLng!.lng,
        zoom: zoom,
      };

      if (
        navigationConfiguration.updateMetadata &&
        navigationConfiguration.stepId
      ) {
        const stepUpdated = navigationConfiguration.updateMetadata(
          updatedCollision,
          navigationConfiguration.stepId!,
          navigationConfiguration.stageCompleted
            ? navigationConfiguration.stageCompleted
            : false,
          !navigationConfiguration.stageCompleted,
        );

        if (stepUpdated && updatedCollision.metadata) {
          updatedCollision.metadata = {
            ...updatedCollision.metadata,
            formState: stepUpdated,
          };
        }
      }

      dispatch(updateCollision(updatedCollision));
      if (navigationConfiguration.save) {
        navigationConfiguration.save();
      }
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <h2 className="location-screen">Collision location</h2>
      <GoASpacer vSpacing="m" />
      {stateCollision.collision.location?.useLocationServices
        ? "Your current location has been detected and shown on the map. If it's not accurate, you can adjust it by moving the pin, zooming in/out, or using the search field to find the collision location."
        : "You've indicated that you're away from the collision location. The map pin has been set to a default location in Alberta, and you will need to manually search for the collision location. You can move the pin, zoom in or out, or use the search field to find it."}
      <GoASpacer vSpacing="xl" />
      <div>
        <CollisionMapComponent
          defaultLocation={DEFAULT_LOCATION}
          updateLatLng={setLatLng}
          updateLocationString={setLocationString}
          updateZoom={setZoom}
          updateInAlberta={(inAlberta) => {
            setEnableSave(inAlberta);
          }}
          latLng={latLng}
          zoom={zoom}
          apiKey={apiKey}
        />
      </div>
      <GoASpacer vSpacing="2xl" />
      <GoAButton
        disabled={!enableSave}
        onClick={() => {
          handleConfirmLocation();
        }}
      >
        Confirm this location
      </GoAButton>

      {modalType === 'confirm' ? (
        <GoAModal
          maxWidth={isDesktop ? '500px' : '343px'}
          heading="Confirm location details"
          role="alertdialog"
          open={showModal}
        >
          <div style={{ paddingRight: '5px', paddingLeft: '5px' }}>
            {locationString}
          </div>
          <GoASpacer vSpacing="m" />
          <GoAButtonGroup alignment="end">
            <div style={{ maxWidth: '250px' }}>
              <GoAButton
                type="tertiary"
                onClick={() => {
                  setShowModal(false);
                }}
              >
                Cancel
              </GoAButton>
            </div>
            <div style={{ maxWidth: '250px' }}>
              <GoAButton
                onClick={() => {
                  save();
                  navigationConfiguration.navigate(nextPage);
                }}
              >
                Confirm
              </GoAButton>
            </div>
          </GoAButtonGroup>
        </GoAModal>
      ) : (
        <GoAModal
          maxWidth={isDesktop ? '500px' : '343px'}
          heading="Invalid location"
          role="alertdialog"
          open={showModal}
        >
          <div style={{ padding: '0 5px' }}>
            Our service is only for select locations and has not been rolled out
            to your service location, please report to your local law
            enforcement detachment.
          </div>
          <GoASpacer vSpacing="m" />
          <GoAButtonGroup alignment="end">
            <div style={{ maxWidth: '250px' }}>
              <GoAButton
                onClick={() => {
                  setShowModal(false);
                  navigate('/dashboard', {
                    replace: true,
                  });
                }}
              >
                Exit service
              </GoAButton>
            </div>
            <div style={{ maxWidth: '250px' }}>
              <GoAButton type="tertiary" onClick={() => setShowModal(false)}>
                Back to location selection
              </GoAButton>
            </div>
          </GoAButtonGroup>
        </GoAModal>
      )}
    </div>
  );
};

export interface CollisionMapComponentProps {
  defaultLocation?: { lat: number; lng: number };
  updateLocationString: (address: string) => void;
  updateLatLng: ({ lat, lng }: any, inAlberta?: boolean) => void;
  updateInAlberta?: (inAlberta: boolean) => void;
  updateZoom: (zoom: number) => void;
  latLng?: { lat: number; lng: number };
  zoom: number;
  apiKey: string;
  className?: string;
}

export const DEFAULT_LOCATION = {
  lat: 52.205598002742875,
  lng: -113.81509646823338,
};

export const CollisionMapComponent = (props: CollisionMapComponentProps) => {
  return (
    <APIProvider apiKey={props.apiKey} onLoad={() => {}}>
      <CollisionMap {...props} />
    </APIProvider>
  );
};

export const CollisionMap = ({
  defaultLocation,
  updateLocationString,
  updateZoom,
  updateLatLng,
  updateInAlberta,
  latLng,
  zoom,
  apiKey,
  className,
}: CollisionMapComponentProps) => {
  const geocoder = useMapsLibrary('geocoding');

  const places = useMapsLibrary('places');

  const map = useMap();

  const albertaGeoJsonFile = `${ROOT_URL}/assets/alberta.geojson`;

  const isDesktop = useIsDesktop();

  const [searchAddress, setSearchAddress] = useState('');
  const [locationString, setLocationString] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [mapLatLong, setMapLatLng] = useState<{ lat: number; lng: number }>();
  const [searchAddressQuery, setSearchAddressQuery] = useState('');

  // MATH! This is the haversine formula to calculate the distance between two points on the earth
  function haversineDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
  ) {
    const toRad = (angle: number) => (Math.PI / 180) * angle;

    const R = 6371;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function findClosestLocation(
    referenceLat: number,
    referenceLon: number,
    locations: any,
  ) {
    return locations.reduce(
      (closest: any, location: any) => {
        const distance = haversineDistance(
          referenceLat,
          referenceLon,
          location.geometry.location.lat(),
          location.geometry.location.lng(),
        );
        console.log(location.formatted_address, distance);
        return distance < closest.distance ? { location, distance } : closest;
      },
      { location: null, distance: Infinity },
    ).location;
  }

  useEffect(() => {
    const queryPlaces = async () => {
      if (places && searchAddressQuery) {
        const p = new places.PlacesService(map!);

        p.findPlaceFromQuery(
          {
            query: searchAddressQuery,

            fields: ['geometry'],
          },
          (results, status) => {
            console.log(results);
            console.log(status);
            if (
              status == 'OK' &&
              results &&
              results[0] &&
              results[0].geometry &&
              results[0].geometry.location
            ) {
              const center = results[0].geometry.location;
              map!.setCenter({ lat: center.lat(), lng: center.lng() });
              updateLatLng({ lat: center.lat(), lng: center.lng() });
              setMapLatLng({ lat: center.lat(), lng: center.lng() });
            }
          },
        );
      }
    };

    queryPlaces();
  }, [searchAddressQuery]);

  useEffect(() => {
    if (geocoder && latLng) {
      const g = new geocoder!.Geocoder();

      g.geocode(
        {
          location: { lat: latLng.lat, lng: latLng.lng },
        },
        (results, status) => {
          if (results) {
            const filteredResults = results.filter((result) =>
              result.types.some((type) =>
                ['street_address', 'route'].includes(type),
              ),
            );

            const closest = findClosestLocation(
              latLng.lat,
              latLng.lng,
              filteredResults,
            );

            if (closest) {
              updateLocationAddress(closest.formatted_address);
            } else {
              updateLocationAddress('Location not found');
            }
          }
        },
      );
    }
  }, [geocoder, latLng]);

  useEffect(() => {
    if (isInitialLoad && latLng && latLng.lat != 0 && latLng.lng != 0) {
      updateLocationAddress('', latLng.lat, latLng.lng);
      setIsInitialLoad(false);
      setMapLatLng(latLng);
    }
  }, [isInitialLoad, latLng]);

  useEffect(() => {
    if (map) {
      map.data.loadGeoJson(albertaGeoJsonFile, null, (features) => {
        const geometry = features[0].getGeometry();

        if (geometry!.getType() === 'Polygon') {
          const polygon: any[] = [];

          geometry!.forEachLatLng((coord) => {
            polygon.push({
              lat: coord.lat(),
              lng: coord.lng(),
            });
          });

          map.addListener('tilesloaded', async () => {
            await runGeofenceCheck(map, polygon);
          });

          map.addListener('dragend', async () => {
            await runGeofenceCheck(map, polygon);
          });
        }
      });

      map.data.setStyle({
        fillColor: 'transparent',
        strokeWeight: 2,
        strokeColor: 'blue',
      });
    }
  }, [map]);

  const runGeofenceCheck = async (map: google.maps.Map, polygon: any[]) => {
    const center = map.getCenter();

    const insideAlberta = google.maps.geometry.poly.containsLocation(
      { lat: center!.lat(), lng: center!.lng() },
      new google.maps.Polygon({ paths: polygon }),
    );

    if (updateInAlberta) updateInAlberta!(insideAlberta);

    if (insideAlberta) {
      updateLocationAddress('', center!.lat(), center!.lng());
    } else {
      updateLocationAddress('Location must be in Alberta');
    }
  };

  const updateLocationAddress = async (
    addressOverrideString?: string,
    lat?: number,
    lng?: number,
  ) => {
    if (addressOverrideString) {
      setLocationString(addressOverrideString);
      updateLocationString(addressOverrideString);
    }
  };

  const zoomIn = () => {
    updateZoom(zoom + 1);
  };

  const zoomOut = () => {
    updateZoom(zoom - 1);
  };

  return (
    <div>
      {latLng ? (
        <Map
          className={className ? className : styles.map}
          styles={[{ stylers: [{ saturation: '-100' }] }]}
          mapTypeId="roadmap"
          defaultZoom={zoom}
          zoom={zoom}
          defaultCenter={mapLatLong ?? defaultLocation}
          center={mapLatLong ?? defaultLocation}
          gestureHandling={'greedy'}
          disableDefaultUI={true}
          onZoomChanged={(e) => {
            updateZoom(map!.getZoom()!);
          }}
          onDragend={(e) => {
            const center = e.map.getCenter();

            if (center) {
              updateLatLng({
                lat: center.lat(),
                lng: center.lng(),
              });
            }
          }}
          onCameraChanged={async (e) => {
            const center = e.map.getCenter();

            if (center) {
              setMapLatLng({
                lat: center.lat(),
                lng: center.lng(),
              });
            }
          }}
        >
          <MapControl position={ControlPosition.CENTER}>
            <GoAIcon type="location" size="large" theme="filled"></GoAIcon>
          </MapControl>
          <MapControl position={ControlPosition.TOP_RIGHT}>
            <div className={styles.topright}>
              <div className={styles.searchcontrol}>
                <GoAInputText
                  width={isDesktop ? '277px' : '229px'} // this could not be done in css as far as i can tell
                  name="searchInput"
                  value={searchAddress}
                  leadingIcon="search"
                  onChange={(item, value) => {
                    setSearchAddress(value);
                  }}
                ></GoAInputText>
                <div className={styles.searchbutton}>
                  <GoAButton
                    onClick={() => {
                      if (searchAddress) {
                        setSearchAddressQuery(searchAddress);
                        setSearchAddress('');
                      }
                    }}
                  >
                    Search
                  </GoAButton>
                </div>
              </div>
              <div className={styles.zoomcontrol}>
                <button
                  onClick={() => {
                    zoomIn();
                  }}
                  className={styles.zoombtn}
                >
                  +
                </button>
                <hr />
                <button
                  onClick={() => {
                    zoomOut();
                  }}
                  className={styles.zoombtn}
                >
                  -
                </button>
              </div>
            </div>
          </MapControl>

          <MapControl position={ControlPosition.BOTTOM_CENTER}>
            <div className={styles.locationcard}>
              <GoASpacer vSpacing="s" />
              <div className={styles.locationcardtext}>Collision location</div>
              <GoASpacer vSpacing="xs" />
              <div className={styles.locationcardtext}>
                <strong>{locationString}</strong>
              </div>
              <GoASpacer vSpacing="m" />
            </div>
          </MapControl>
        </Map>
      ) : (
        'loading map...'
      )}
    </div>
  );
};
